"use client";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import DownloadIcon from "@mui/icons-material/Download";
import React from "react";
import type {
  GetSongActions,
  SongAction,
} from "~/components/audio/songActions.types";
import SongRenameDialog from "~/components/audio/SongRenameDialog";
import YotoIcon from "~/components/icons/YotoIcon";
import { ShareSongDialog } from "~/components/ShareSongDialog";
import { customerSongsService } from "~/connectors/songs.connector";
import type { Song } from "~/types";
import ReviewThumbsButtonsAction from "./ReviewThumbsButtonsAction";


export const getCustomerSongActions: GetSongActions = (
  song: Song,
  { router, getState },
): SongAction[] => {
  const handleDownload = async () => {
    try {
      await customerSongsService.downloadSong(song.id);
    } catch (err) {
      console.error("Download error:", err);
      alert("Eroare la descărcarea fișierului");
    }
  };

  const handleRename = async (songId: string, title: string) => {
    await customerSongsService.renameSong(songId, title);
    getState().updateCurrentSong({ title });
  };

  const handleYotoClick = () => {
    getState().setBigPlayerOpen(false);
    router.push(
      song.yotoAvailable || song.yotoExcluded ? "/yoto" : "/yoto/despre",
    );
  };

  const yotoStartIcon = song.yotoExcluded
    ? React.createElement(DoNotDisturbOnIcon)
    : song.yotoAvailable && song.yotoPixelArtSrc
      ? React.createElement("img", {
          src: song.yotoPixelArtSrc,
          alt: "Yoto",
          style: { width: 18, height: 18, borderRadius: 2, objectFit: "cover" as const },
        })
      : song.yotoAvailable
        ? React.createElement(CheckCircleIcon)
        : React.createElement(YotoIcon, { size: 18 });

  const actions: SongAction[] = [
    {
      CustomButtonComponent: ReviewThumbsButtonsAction,
      visible: !!song.orderId,
    },
    { panel: "lyrics", visible: !!song.lyrics },
    {
      buttonProps: {
        startIcon: React.createElement(DownloadIcon),
        children: "Descarcă",
        onClick: () => void handleDownload(),
      },
      visible: !!song.downloadable,
    },
    {
      buttonProps: {
        startIcon: React.createElement(CardGiftcardIcon),
        children: "Cadou",
        // onClick is intentionally left to the player to handle DialogComponent if present
        onClick: () => {
          // fallback: close big player and open the order page if dialog wiring fails
          getState().setBigPlayerOpen(false);
          router.push(`/dashboard/comenzi/${song.orderId}`);
        },
      },
      DialogComponent: ({ song: s, onClose }) =>
        React.createElement(ShareSongDialog, {
          open: true,
          onClose,
          orderId: s.orderId!,
          darkMode: true,
        }),
      visible: !song.isShared && !!song.orderId,
    },
    {
      buttonProps: {
        startIcon: yotoStartIcon,
        children: "Yoto",
        onClick: handleYotoClick,
      },
      visible: !!song.yotoCompatible,
    },
    {
      inContextMenu: true,
      contextMenuLabel: "Schimbă titlul",
      DialogComponent: ({ song: s, onClose }) =>
        React.createElement(SongRenameDialog, {
          song: s,
          onClose,
          onSave: handleRename,
        }),
      visible: !song.isShared,
    },
    {
      inContextMenu: true,
      contextMenuLabel: "Vezi comanda",
      onContextMenuClick: () => {
        getState().setBigPlayerOpen(false);
        router.push(`/dashboard/comenzi/${song.orderId}`);
      },
      visible: !song.isShared && !!song.orderId,
    },
  ];

  return actions;
};
